import {
  ICD_REASON,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import Translations from "translations/types";
import { z } from "zod";

export const DRG_CODE_CHARACTER_LIMIT = 4;
export const ICD_CODE_CHARACTER_LIMIT = 6;
export const IK_NUMBER_CHARACTER_LIMIT = 9;
export const INFECTIONS_CHARACTER_LIMIT = 150;
export const PENSION_INSURANCE_NUMBER_CHARACTER_LIMIT = 12;
export const SEND_DOCUMENTS_CHARACTER_LIMIT = 34;

const ALLOWED_FLOOR_VALUES = ["u", "0", "h", "s"];

// Time schemas
export const unixDateSchema = z.number().int();
export const AHBdateSchema = z.string().regex(/^\d{8}$/); // YYYYMMDD format
export const AHBtimeSchema = z.string().regex(/^\d{6}$/); // HHMMSS format

// Address schemas
export const AHBpostleitzahlSchema = z.string().max(10);
export const AHBfloorSchema = (translations?: Translations) =>
  z
    .string()
    .refine(
      (value) =>
        ALLOWED_FLOOR_VALUES.includes(value.toLowerCase()) ||
        /^\d{1,2}$/.test(value),
      {
        message: translations
          ? translations.patientForms.generalForm.sectionI.errorLivingSituation
          : 'Invalid Stockwerk value. Must be "u", "0", "h", "s", or a 1-2 digit number.',
      },
    );

// Yes/no/consent/predicament schemas
export const consentYesOnlySchema = z.literal(PREDICAMENT_STATE_YES);
export const predicamentSchema = z.union([
  z.literal(PREDICAMENT_STATE_YES),
  z.literal(PREDICAMENT_STATE_NO),
]);

export const predicamentYesNoUnknownSchema = z.union([
  z.literal(PREDICAMENT_STATE_YES),
  z.literal(PREDICAMENT_STATE_NO),
  z.literal(PREDICAMENT_STATE_UNKNOWN),
]);

export const ahbDateinummerSchema = z.coerce.number().int().min(1).max(999999);

/**
 * IK-number Identifies the entire healthcare institution (e.g. hospital, rehab clinic)
 */
export const IKNumberSchema = z
  .union([z.string().length(9), z.number().int().gte(100000000).lte(999999999)])
  .transform((val) => val.toString());
/**
 * Standortnummer identifies a specific location or site of a hospital
 */
export type SiteIdNumber = z.infer<typeof siteIdSchema>;
export const siteIdSchema = z
  .string()
  .nonempty()
  .regex(/^\d{9}$/, {
    message: "Site Number must be exactly 9 digits.",
  });

// ICD code schames
export const ICDReasonSchema = z.nativeEnum(ICD_REASON);
export type ICDReason = z.infer<typeof ICDReasonSchema>;
export const ICDCodeSchema = z
  .string()
  .min(3, "ICD-10 code must be at least 3 characters.")
  .max(6, "ICD-10 code must be at most 6 characters.")
  .refine((val) => !val.includes("."), {
    message: "ICD-10 code must not contain dots.",
  });

type ValidationMessageType = {
  invalid?: string;
  invalidUnion?: string;
  required?: string;
  tooLong?: (maxLength: number | bigint) => string;
  tooShort?: (minLength: number | bigint) => string;
};

export type ValidationMessagesMap<Schema extends z.ZodTypeAny = any> = {
  [K in keyof z.infer<Schema> as string]?: ValidationMessageType;
} & {
  default?: ValidationMessageType;
};

export const createErrorMap = <Schema extends z.ZodTypeAny>(
  messages: ValidationMessagesMap<Schema>,
): z.ZodErrorMap => {
  return (issue, ctx) => {
    const fieldPath = issue.path.join(".");

    if (issue.code === z.ZodIssueCode.invalid_type) {
      if (
        issue.expected === "string" &&
        (issue.received === "null" || issue.received === "undefined")
      ) {
        const fieldMessage = messages[fieldPath]?.required;
        if (fieldMessage) {
          return { message: fieldMessage };
        }

        const defaultMessage = messages.default?.required;
        if (defaultMessage) {
          return { message: defaultMessage };
        }
      }

      if (
        issue.expected === "number" &&
        (issue.received === "null" || issue.received === "undefined")
      ) {
        const fieldMessage = messages[fieldPath]?.required;
        if (fieldMessage) {
          return { message: fieldMessage };
        }
      }
    }

    if (issue.code === z.ZodIssueCode.too_small) {
      if (issue.type === "string") {
        const minLength = issue.minimum;

        const fieldMessage = messages[fieldPath]?.tooShort;
        if (fieldMessage) {
          return { message: fieldMessage(minLength) };
        }

        const defaultMessage = messages.default?.tooShort;
        if (defaultMessage) {
          return { message: defaultMessage(minLength) };
        }

        return {
          message: `It must be at least ${minLength} characters`,
        };
      }

      const fieldMessage = messages[fieldPath]?.required;
      if (fieldMessage) {
        return { message: fieldMessage };
      }

      const minLength = issue.minimum;
      return {
        message: `It must be at least ${minLength} characters`,
      };
    }

    if (issue.code === z.ZodIssueCode.too_big) {
      if (issue.type === "string") {
        const maxLength = issue.maximum;

        const fieldMessage = messages[fieldPath]?.tooLong;
        if (fieldMessage) {
          return { message: fieldMessage(maxLength) };
        }

        const defaultMessage = messages.default?.tooLong;
        if (defaultMessage) {
          return { message: defaultMessage(maxLength) };
        }
      }
    }

    if (issue.code === z.ZodIssueCode.invalid_union) {
      const fieldInvalidUnionMessage = messages[fieldPath]?.invalidUnion;
      if (fieldInvalidUnionMessage) {
        return { message: fieldInvalidUnionMessage };
      }

      const fieldRequiredMessage = messages[fieldPath]?.required;
      if (fieldRequiredMessage) {
        return { message: fieldRequiredMessage };
      }

      const defaultInvalidUnionMessage = messages.default?.invalidUnion;
      if (defaultInvalidUnionMessage) {
        return { message: defaultInvalidUnionMessage };
      }
    }

    return { message: ctx.defaultError };
  };
};
